import { useEffect, useState } from "react";
import { supabase } from "../../../../lib/supabase";
import { useAuth } from "../../../../hooks/useAuth";
import DashboardLayout from "../../../../components/feature/DashboardLayout";

const navItems = [
  { label: "Dashboard", path: "/dashboard/client", icon: "ri-dashboard-line" },
  { label: "Appointments", path: "/dashboard/client/appointments", icon: "ri-calendar-event-line" },
  { label: "Messages", path: "/dashboard/client/messages", icon: "ri-chat-3-line" },
  { label: "Settings", path: "/dashboard/client/settings", icon: "ri-settings-3-line" },
];

interface ProfileData {
  full_name: string | null;
  phone: string | null;
  address: string | null;
  role: string;
  created_at: string;
}

export default function ClientSettings() {
  const { user } = useAuth();
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [form, setForm] = useState({
    full_name: "",
    phone: "",
    address: "",
  });

  useEffect(() => {
    if (!user) return;
    const fetchProfile = async () => {
      const { data } = await supabase
        .from("profiles")
        .select("full_name, phone, address, role, created_at")
        .eq("id", user.id)
        .maybeSingle();
      if (data) {
        setProfile(data as ProfileData);
        setForm({
          full_name: data.full_name || "",
          phone: data.phone || "",
          address: data.address || "",
        });
      }
      setLoading(false);
    };
    fetchProfile();
  }, [user]);

  const handleChange = (key: string, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSaving(true);
    setMessage("");
    const { error } = await supabase
      .from("profiles")
      .update({
        full_name: form.full_name,
        phone: form.phone,
        address: form.address,
      })
      .eq("id", user!.id);

    if (error) {
      setMessage("Failed to save changes.");
    } else {
      setMessage("Profile updated successfully!");
    }
    setSaving(false);
  };

  return (
    <DashboardLayout navItems={navItems} title="Client Portal" subtitle="Settings">
      {loading ? (
        <div className="flex items-center gap-3 py-12">
          <div className="w-5 h-5 border-2 border-brand-green-600 border-t-transparent rounded-full animate-spin" />
          <span className="text-sm text-gray-500">Loading settings...</span>
        </div>
      ) : (
        <div className="max-w-2xl">
          {/* Profile Card */}
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden mb-6">
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-brand-green-100 flex items-center justify-center shrink-0">
                  <div className="w-7 h-7 flex items-center justify-center">
                    <i className="ri-user-line text-brand-green-600 text-xl" />
                  </div>
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">{profile?.full_name || "Client"}</h2>
                  <p className="text-sm text-gray-500">{user?.email}</p>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-5">
              {message && (
                <div className={`rounded-lg p-3 border ${message.includes("success") ? "bg-brand-green-50 border-brand-green-100 text-brand-green-800" : "bg-red-50 border-red-100 text-red-700"}`}>
                  <p className="text-sm text-center">{message}</p>
                </div>
              )}

              <div>
                <label htmlFor="full_name" className="block text-xs font-medium text-gray-700 mb-1.5">
                  Full Name
                </label>
                <input
                  id="full_name"
                  type="text"
                  value={form.full_name}
                  onChange={(e) => handleChange("full_name", e.target.value)}
                  className="w-full px-3 py-2.5 text-sm rounded-lg border border-gray-200 focus:border-brand-green-400 focus:ring-2 focus:ring-brand-green-100 outline-none transition-all"
                  placeholder="Your full name"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-xs font-medium text-gray-700 mb-1.5">
                  Phone Number
                </label>
                <input
                  id="phone"
                  type="tel"
                  value={form.phone}
                  onChange={(e) => handleChange("phone", e.target.value)}
                  className="w-full px-3 py-2.5 text-sm rounded-lg border border-gray-200 focus:border-brand-green-400 focus:ring-2 focus:ring-brand-green-100 outline-none transition-all"
                  placeholder="773-xxx-xxxx"
                />
              </div>

              <div>
                <label htmlFor="address" className="block text-xs font-medium text-gray-700 mb-1.5">
                  Address
                </label>
                <textarea
                  id="address"
                  rows={3}
                  maxLength={500}
                  value={form.address}
                  onChange={(e) => handleChange("address", e.target.value)}
                  className="w-full px-3 py-2.5 text-sm rounded-lg border border-gray-200 focus:border-brand-green-400 focus:ring-2 focus:ring-brand-green-100 outline-none resize-none transition-all"
                  placeholder="Your address"
                />
              </div>

              <div className="pt-2">
                <button
                  type="submit"
                  disabled={saving}
                  className="px-6 py-2.5 bg-brand-green-600 text-white text-sm font-semibold rounded-lg hover:bg-brand-green-700 disabled:bg-brand-green-400 transition-all whitespace-nowrap"
                >
                  {saving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>

          {/* Account Info */}
          <div className="bg-white rounded-2xl border border-gray-100 p-6">
            <h3 className="text-sm font-semibold text-gray-900 mb-4 uppercase tracking-wider">Account Info</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between py-2 border-b border-gray-50">
                <span className="text-sm text-gray-500">Email</span>
                <span className="text-sm text-gray-900">{user?.email}</span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-gray-50">
                <span className="text-sm text-gray-500">Role</span>
                <span className="text-sm text-gray-900 capitalize">{profile?.role || "Client"}</span>
              </div>
              <div className="flex items-center justify-between py-2">
                <span className="text-sm text-gray-500">Member Since</span>
                <span className="text-sm text-gray-900">
                  {profile?.created_at
                    ? new Date(profile.created_at).toLocaleDateString("en-US", { month: "long", year: "numeric" })
                    : "N/A"}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}